/**
 * Cart Delivery Service
 * Adapts UnifiedDeliveryCalculationService for Cart item delivery method management
 * Handles per-item delivery calculations and date management
 */

import {
  UnifiedDeliveryCalculationService,
  DeliveryInput,
  DeliveryMethodAvailability,
} from "./UnifiedDeliveryCalculationService";
import { getUnifiedDeliveryData } from "../lib/unifiedDeliveryService";
import { DateService } from "../utils/dateUtils";

export interface CartItemDeliveryOptions {
  express: {
    available: boolean;
    autoDate: string | null; // YYYY-MM-DD format for backend
    reason?: string;
  };
  rapido: {
    available: boolean;
    availableDates: string[]; // YYYY-MM-DD format for backend
    suggestedDate?: string; // First available date
    reason?: string;
  };
  standard: {
    available: boolean;
    earliestDate: string | null; // YYYY-MM-DD format for backend
    requiresUserSelection: boolean;
    reason?: string;
  };
}

export class CartDeliveryService {
  /**
   * Calculate delivery options for a cart item
   * Similar to Product calculation but with cart-specific formatting
   */
  static async calculateCartItemDeliveryOptions(
    cartItem: any,
    destinationLeadTimeZoneId: string,
    destinationFeeZoneId: string
  ): Promise<CartItemDeliveryOptions> {
    try {
      const product = cartItem.product;
      const logistics = product.logistics;
      const partner = product.partner;

      if (!logistics || !partner?.daysOpen) {
        console.warn(`⚠️ [CART-DELIVERY-SERVICE] Missing required data:`, {
          cartCustomId: cartItem.cartCustomId,
          missingLogistics: !logistics,
          missingDaysOpen: !partner?.daysOpen,
          reason: "Insufficient data for delivery calculation",
        });
        return this.getUnavailableResult("Información de logística incompleta");
      }

      // Get route availability from backend
      const deliveryData = await getUnifiedDeliveryData({
        originFeeZoneId: partner.deliveryFeeZone?.documentId,
        originLeadTimeZoneId: partner.deliveryLeadTimeZone?.documentId,
        destinationFeeZoneId,
        destinationLeadTimeZoneId,
      });

      if (!deliveryData?.deliveryMethods) {
        console.warn(
          `⚠️ [CART-DELIVERY-SERVICE] No delivery methods available:`,
          {
            cartCustomId: cartItem.cartCustomId,
            deliveryData: deliveryData,
            reason: "No delivery methods available for this route",
          }
        );
        return this.getUnavailableResult(
          "Métodos de entrega no disponibles para esta ruta"
        );
      }

      // Prepare input for unified service
      const input: DeliveryInput = {
        logistics: {
          expressDelivery: logistics.expressDelivery === true,
          limitTime: logistics.limitTime || "14:00",
          daysToDeliver:
            logistics.minDaysToDeliver || logistics.daysToDeliver || 1,
          minDaysToDeliver: logistics.minDaysToDeliver,
          weight: logistics.weight,
        },
        partnerSchedule: partner.daysOpen,
        zones: {
          originFeeZoneId: partner.deliveryFeeZone?.documentId,
          originLeadTimeZoneId: partner.deliveryLeadTimeZone?.documentId,
          destinationFeeZoneId,
          destinationLeadTimeZoneId,
        },
        opsLeadTime: deliveryData.opsLeadTime.opsLeadTime || 1,
      };

      const routeAvailability: DeliveryMethodAvailability = {
        express: !!deliveryData.deliveryMethods.express,
        rapido: !!deliveryData.deliveryMethods.scheduled,
        standard: !!deliveryData.deliveryMethods.standard,
      };

      // Calculate delivery options
      const result =
        await UnifiedDeliveryCalculationService.calculateDeliveryOptions(
          input,
          routeAvailability
        );

      // Format for Cart system usage
      const formattedResult = {
        express: {
          available: result.express.available,
          autoDate:
            result.express.available && result.express.deliveryDate
              ? this.formatDateForBackend(result.express.deliveryDate)
              : null,
          reason: result.express.reason,
        },
        rapido: {
          available: result.rapido.available,
          availableDates: result.rapido.availableDates.map((date) =>
            this.formatDateForBackend(date)
          ),
          suggestedDate:
            result.rapido.availableDates.length > 0
              ? this.formatDateForBackend(result.rapido.availableDates[0])
              : undefined,
          reason: result.rapido.reason,
        },
        standard: {
          available: result.standard.available,
          earliestDate: result.standard.earliestDate
            ? this.formatDateForBackend(result.standard.earliestDate)
            : null,
          requiresUserSelection: true, // Standard always requires user selection
          reason: result.standard.reason,
        },
      };

      return formattedResult;
    } catch (error) {
      console.error("Error calculating cart item delivery options:", error);
      return this.getUnavailableResult("Error al calcular opciones de entrega");
    }
  }

  /**
   * Get suggested delivery date based on selected method
   * Used when user changes delivery method in Cart
   */
  static async getSuggestedDeliveryDate(
    cartItem: any,
    selectedMethod: "express" | "rapido" | "standard",
    destinationLeadTimeZoneId: string,
    destinationFeeZoneId: string
  ): Promise<string | null> {
    const options = await this.calculateCartItemDeliveryOptions(
      cartItem,
      destinationLeadTimeZoneId,
      destinationFeeZoneId
    );

    switch (selectedMethod) {
      case "express":
        return options.express.autoDate;

      case "rapido":
        return options.rapido.suggestedDate || null;

      case "standard":
        return null; // Standard requires user selection

      default:
        return null;
    }
  }

  /**
   * Validate if delivery method is available for cart item
   */
  static async validateDeliveryMethodForItem(
    cartItem: any,
    method: "express" | "rapido" | "standard",
    destinationLeadTimeZoneId: string,
    destinationFeeZoneId: string
  ): Promise<{ valid: boolean; reason?: string }> {
    const options = await this.calculateCartItemDeliveryOptions(
      cartItem,
      destinationLeadTimeZoneId,
      destinationFeeZoneId
    );

    switch (method) {
      case "express":
        return {
          valid: options.express.available,
          reason: options.express.reason,
        };

      case "rapido":
        return {
          valid: options.rapido.available,
          reason: options.rapido.reason,
        };

      case "standard":
        return {
          valid: options.standard.available,
          reason: options.standard.reason,
        };

      default:
        return { valid: false, reason: "Método de entrega no válido" };
    }
  }

  /**
   * Calculate earliest delivery date for group of items (used for DeliveryDatePicker)
   */
  static async calculateGroupEarliestDeliveryDate(
    cartItems: any[],
    destinationLeadTimeZoneId: string,
    destinationFeeZoneId: string
  ): Promise<Date> {
    let latestEarliestDate: Date = new Date();

    // Calculate for each item and get the latest earliest date
    for (const item of cartItems) {
      const options = await this.calculateCartItemDeliveryOptions(
        item,
        destinationLeadTimeZoneId,
        destinationFeeZoneId
      );

      if (options.standard.available && options.standard.earliestDate) {
        const itemEarliestDate = DateService.createDateFromApiFormat(
          options.standard.earliestDate
        );
        if (itemEarliestDate > latestEarliestDate) {
          latestEarliestDate = itemEarliestDate;
        }
      }
    }

    return latestEarliestDate;
  }

  /**
   * Format date for backend (YYYY-MM-DD)
   */
  private static formatDateForBackend(date: Date): string {
    const formatted = date.toISOString().split("T")[0];
    return formatted;
  }

  /**
   * Get result when delivery options are unavailable
   */
  private static getUnavailableResult(reason: string): CartItemDeliveryOptions {
    return {
      express: { available: false, autoDate: null, reason },
      rapido: { available: false, availableDates: [], reason },
      standard: {
        available: false,
        earliestDate: null,
        requiresUserSelection: false,
        reason,
      },
    };
  }

  /**
   * Validate if a saved delivery date and method are still possible
   * Returns validation result with corrective actions if needed
   */
  static async validateSavedDeliveryDate(
    cartItem: any,
    savedDeliveryDate: string, // YYYY-MM-DD format
    savedDeliveryMethod: "express" | "rapido" | "standard",
    destinationLeadTimeZoneId: string,
    destinationFeeZoneId: string
  ): Promise<{
    isValid: boolean;
    reason?: string;
    suggestedDate?: string;
    suggestedMethod?: "express" | "rapido" | "standard";
    needsUpdate: boolean;
  }> {
    try {
      const options = await this.calculateCartItemDeliveryOptions(
        cartItem,
        destinationLeadTimeZoneId,
        destinationFeeZoneId
      );

      const savedDate = DateService.createDateFromApiFormat(savedDeliveryDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Check if saved date is in the past
      if (savedDate < today) {
        const suggestedMethod = this.getSuggestedMethodFromOptions(options);
        const suggestedDate = await this.getSuggestedDeliveryDate(
          cartItem,
          suggestedMethod,
          destinationLeadTimeZoneId,
          destinationFeeZoneId
        );

        return {
          isValid: false,
          reason: "La fecha guardada ya pasó",
          suggestedDate: suggestedDate || undefined,
          suggestedMethod,
          needsUpdate: true,
        };
      }

      // Validate based on delivery method
      switch (savedDeliveryMethod) {
        case "express":
          return this.validateExpressDate(savedDate, options);

        case "rapido":
          return this.validateRapidoDate(savedDate, options);

        case "standard":
          return this.validateStandardDate(savedDate, options);

        default:
          return {
            isValid: false,
            reason: "Método de entrega no válido",
            needsUpdate: true,
          };
      }
    } catch (error) {
      console.error("Error validating saved delivery date:", error);
      return {
        isValid: false,
        reason: "Error al validar fecha de entrega",
        needsUpdate: true,
      };
    }
  }

  /**
   * Validate express delivery date
   */
  private static validateExpressDate(
    savedDate: Date,
    options: CartItemDeliveryOptions
  ): {
    isValid: boolean;
    reason?: string;
    suggestedDate?: string;
    suggestedMethod?: "express" | "rapido" | "standard";
    needsUpdate: boolean;
  } {
    if (!options.express.available) {
      // Express no longer available, suggest next best option
      if (options.rapido.available && options.rapido.suggestedDate) {
        return {
          isValid: false,
          reason: "Entrega express ya no está disponible",
          suggestedDate: options.rapido.suggestedDate,
          suggestedMethod: "rapido",
          needsUpdate: true,
        };
      } else if (options.standard.available && options.standard.earliestDate) {
        return {
          isValid: false,
          reason: "Entrega express ya no está disponible",
          suggestedDate: options.standard.earliestDate,
          suggestedMethod: "standard",
          needsUpdate: true,
        };
      }

      return {
        isValid: false,
        reason: "Entrega express ya no está disponible",
        needsUpdate: true,
      };
    }

    // For express, check if auto date matches saved date
    if (options.express.autoDate) {
      const autoDate = DateService.createDateFromApiFormat(
        options.express.autoDate
      );
      const isSameDay = autoDate.toDateString() === savedDate.toDateString();

      if (!isSameDay) {
        return {
          isValid: false,
          reason: "La fecha de entrega express ha cambiado",
          suggestedDate: options.express.autoDate,
          suggestedMethod: "express",
          needsUpdate: true,
        };
      }
    }

    return { isValid: true, needsUpdate: false };
  }

  /**
   * Validate rapido delivery date
   */
  private static validateRapidoDate(
    savedDate: Date,
    options: CartItemDeliveryOptions
  ): {
    isValid: boolean;
    reason?: string;
    suggestedDate?: string;
    suggestedMethod?: "express" | "rapido" | "standard";
    needsUpdate: boolean;
  } {
    if (!options.rapido.available) {
      // Rapido no longer available, suggest next best option
      if (options.express.available && options.express.autoDate) {
        return {
          isValid: false,
          reason: "Entrega rápida ya no está disponible",
          suggestedDate: options.express.autoDate,
          suggestedMethod: "express",
          needsUpdate: true,
        };
      } else if (options.standard.available && options.standard.earliestDate) {
        return {
          isValid: false,
          reason: "Entrega rápida ya no está disponible",
          suggestedDate: options.standard.earliestDate,
          suggestedMethod: "standard",
          needsUpdate: true,
        };
      }

      return {
        isValid: false,
        reason: "Entrega rápida ya no está disponible",
        needsUpdate: true,
      };
    }

    // Check if saved date is still in available dates
    const isDateAvailable = options.rapido.availableDates.some((dateStr) => {
      const availableDate = DateService.createDateFromApiFormat(dateStr);
      return availableDate.toDateString() === savedDate.toDateString();
    });

    if (!isDateAvailable) {
      return {
        isValid: false,
        reason: "La fecha rápida guardada ya no está disponible",
        suggestedDate: options.rapido.suggestedDate,
        suggestedMethod: "rapido",
        needsUpdate: true,
      };
    }

    return { isValid: true, needsUpdate: false };
  }

  /**
   * Validate standard delivery date
   */
  private static validateStandardDate(
    savedDate: Date,
    options: CartItemDeliveryOptions
  ): {
    isValid: boolean;
    reason?: string;
    suggestedDate?: string;
    suggestedMethod?: "express" | "rapido" | "standard";
    needsUpdate: boolean;
  } {
    if (!options.standard.available) {
      // Standard no longer available (unlikely but possible)
      if (options.express.available && options.express.autoDate) {
        return {
          isValid: false,
          reason: "Entrega estándar ya no está disponible",
          suggestedDate: options.express.autoDate,
          suggestedMethod: "express",
          needsUpdate: true,
        };
      } else if (options.rapido.available && options.rapido.suggestedDate) {
        return {
          isValid: false,
          reason: "Entrega estándar ya no está disponible",
          suggestedDate: options.rapido.suggestedDate,
          suggestedMethod: "rapido",
          needsUpdate: true,
        };
      }

      return {
        isValid: false,
        reason: "Entrega estándar ya no está disponible",
        needsUpdate: true,
      };
    }

    // Check if saved date is before earliest available date
    if (options.standard.earliestDate) {
      const earliestDate = DateService.createDateFromApiFormat(
        options.standard.earliestDate
      );

      if (savedDate < earliestDate) {
        return {
          isValid: false,
          reason: "La fecha guardada es muy temprana",
          suggestedDate: options.standard.earliestDate,
          suggestedMethod: "standard",
          needsUpdate: true,
        };
      }
    }

    return { isValid: true, needsUpdate: false };
  }

  /**
   * Get suggested delivery method based on available options
   */
  private static getSuggestedMethodFromOptions(
    options: CartItemDeliveryOptions
  ): "express" | "rapido" | "standard" {
    if (options.express.available) return "express";
    if (options.rapido.available) return "rapido";
    return "standard";
  }

  /**
   * Validate entire cart group delivery settings
   */
  static async validateCartGroupDelivery(
    cartGroup: any,
    destinationLeadTimeZoneId: string,
    destinationFeeZoneId: string
  ): Promise<{
    isValid: boolean;
    invalidItems: Array<{
      cartCustomId: string;
      reason: string;
      suggestedDate?: string;
      suggestedMethod?: string;
    }>;
    groupNeedsUpdate: boolean;
  }> {
    if (!cartGroup.deliveryDate || !cartGroup.deliveryMethod) {
      return {
        isValid: false,
        invalidItems: [],
        groupNeedsUpdate: true,
      };
    }

    const invalidItems: Array<{
      cartCustomId: string;
      reason: string;
      suggestedDate?: string;
      suggestedMethod?: string;
    }> = [];

    // Validate each item in the group
    for (const item of cartGroup.items) {
      const validation = await this.validateSavedDeliveryDate(
        item,
        cartGroup.deliveryDate,
        cartGroup.deliveryMethod,
        destinationLeadTimeZoneId,
        destinationFeeZoneId
      );

      if (!validation.isValid) {
        invalidItems.push({
          cartCustomId: item.cartCustomId,
          reason: validation.reason || "Fecha no válida",
          suggestedDate: validation.suggestedDate,
          suggestedMethod: validation.suggestedMethod,
        });
      }
    }

    return {
      isValid: invalidItems.length === 0,
      invalidItems,
      groupNeedsUpdate: invalidItems.length > 0,
    };
  }

  /**
   * Get the appropriate minimum/earliest date for DeliveryDatePicker based on delivery method
   * This addresses the issue where standard delivery date was shown for all methods
   */
  static async getEarliestDateForDeliveryMethod(
    cartItems: any[],
    deliveryMethod: "express" | "rapido" | "standard",
    destinationLeadTimeZoneId: string,
    destinationFeeZoneId: string
  ): Promise<Date> {
    try {
      let earliestDate: Date | null = null; // Start with null, not today
      const itemResults: Array<{
        itemIndex: number;
        cartCustomId: string;
        calculated: boolean;
        date?: Date;
        reason?: string;
      }> = [];

      for (let index = 0; index < cartItems.length; index++) {
        const item = cartItems[index];

        const options = await this.calculateCartItemDeliveryOptions(
          item,
          destinationLeadTimeZoneId,
          destinationFeeZoneId
        );

        let itemEarliestDate: Date | null = null;
        let reason = "";

        switch (deliveryMethod) {
          case "express":
            if (options.express.available && options.express.autoDate) {
              itemEarliestDate = DateService.createDateFromApiFormat(
                options.express.autoDate
              );
              reason = "Express auto date";
            } else {
              reason = options.express.reason || "Express not available";
            }
            break;

          case "rapido":
            if (options.rapido.available && options.rapido.suggestedDate) {
              itemEarliestDate = DateService.createDateFromApiFormat(
                options.rapido.suggestedDate
              );
              reason = "Rapido suggested date";
            } else {
              reason = options.rapido.reason || "Rapido not available";
            }
            break;

          case "standard":
            if (options.standard.available && options.standard.earliestDate) {
              itemEarliestDate = DateService.createDateFromApiFormat(
                options.standard.earliestDate
              );
              reason = "Standard earliest date";
            } else {
              reason = options.standard.reason || "Standard not available";
            }
            break;
        }

        // Record result for this item
        itemResults.push({
          itemIndex: index,
          cartCustomId: item.cartCustomId,
          calculated: !!itemEarliestDate,
          date: itemEarliestDate || undefined,
          reason,
        });

        // Take the latest date among all items (latest constraint wins)
        // Fixed logic: properly handle null initial state
        if (itemEarliestDate) {
          if (!earliestDate || itemEarliestDate > earliestDate) {
            const wasFirstValidDate = !earliestDate;
            const previousDate = earliestDate;
            earliestDate = itemEarliestDate;

            console.log(
              `📅 [CART-DELIVERY-SERVICE] New latest date from item ${
                index + 1
              }:`,
              {
                method: deliveryMethod,
                itemIndex: index,
                cartCustomId: item.cartCustomId,
                comparison: {
                  previousDate: previousDate
                    ? {
                        iso: previousDate.toISOString(),
                        local: previousDate.toLocaleDateString("es-PE"),
                      }
                    : null,
                  newDate: {
                    iso: itemEarliestDate.toISOString(),
                    local: itemEarliestDate.toLocaleDateString("es-PE"),
                    dayName: itemEarliestDate.toLocaleDateString("es-PE", {
                      weekday: "long",
                    }),
                  },
                  wasFirstValidDate,
                  isLaterThanPrevious:
                    !previousDate || itemEarliestDate > previousDate,
                },
                reason,
                businessRule: "Latest (most restrictive) date wins",
              }
            );
          } else {
            console.log(
              `⏭️ [CART-DELIVERY-SERVICE] Item ${
                index + 1
              } date not used (earlier than current):`,
              {
                method: deliveryMethod,
                itemIndex: index,
                cartCustomId: item.cartCustomId,
                comparison: {
                  currentEarliest: {
                    iso: earliestDate.toISOString(),
                    local: earliestDate.toLocaleDateString("es-PE"),
                  },
                  itemDate: {
                    iso: itemEarliestDate.toISOString(),
                    local: itemEarliestDate.toLocaleDateString("es-PE"),
                  },
                  itemIsEarlier: itemEarliestDate <= earliestDate,
                },
                reason,
              }
            );
          }
        } else {
          console.log(
            `❌ [CART-DELIVERY-SERVICE] Item ${index + 1} has no valid date:`,
            {
              method: deliveryMethod,
              itemIndex: index,
              cartCustomId: item.cartCustomId,
              reason,
              impact: "No contribution to final date",
            }
          );
        }
      }

      // If no valid dates found, throw error instead of using fallback
      // According to business rules: "Don't use fallback calculations"
      if (!earliestDate) {
        const errorMessage = `No ${deliveryMethod} delivery dates available for your location`;
        console.error(`❌ [CART-DELIVERY-SERVICE] ${errorMessage}:`, {
          method: deliveryMethod,
          itemResults,
          destinationZones: {
            leadTime: destinationLeadTimeZoneId,
            fee: destinationFeeZoneId,
          },
          businessRule:
            "No fallback calculations allowed - must show error to user",
        });

        throw new Error(errorMessage);
      }

      console.log(
        `✅ [CART-DELIVERY-SERVICE] ${deliveryMethod} calculation complete:`,
        {
          method: deliveryMethod,
          finalDate: {
            iso: earliestDate.toISOString(),
            local: earliestDate.toLocaleDateString("es-PE"),
            dayName: earliestDate.toLocaleDateString("es-PE", {
              weekday: "long",
            }),
          },
          itemResults,
          businessRule: "Latest (most restrictive) date among all items wins",
        }
      );

      return earliestDate;
    } catch (error) {
      console.error(
        "Error calculating earliest date for delivery method:",
        error
      );
      // Fallback to tomorrow
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow;
    }
  }

  /**
   * Get available dates for a specific delivery method across all items in a group
   * Used for DeliveryDatePicker to show only valid dates for the selected method
   */
  static async getAvailableDatesForDeliveryMethod(
    cartItems: any[],
    deliveryMethod: "express" | "rapido" | "standard",
    destinationLeadTimeZoneId: string,
    destinationFeeZoneId: string
  ): Promise<Date[]> {
    try {
      const availableDatesMap = new Map<string, Date>();

      for (const item of cartItems) {
        const options = await this.calculateCartItemDeliveryOptions(
          item,
          destinationLeadTimeZoneId,
          destinationFeeZoneId
        );

        let itemAvailableDates: string[] = [];

        switch (deliveryMethod) {
          case "express":
            if (options.express.available && options.express.autoDate) {
              itemAvailableDates = [options.express.autoDate];
            }
            break;

          case "rapido":
            if (options.rapido.available) {
              itemAvailableDates = options.rapido.availableDates;
            }
            break;

          case "standard":
            // For standard, we need to calculate available dates from earliest date
            if (options.standard.available && options.standard.earliestDate) {
              // Generate dates from earliest date for next 30 days (reasonable limit)
              const earliestDate = DateService.createDateFromApiFormat(
                options.standard.earliestDate
              );
              itemAvailableDates = [];

              for (let i = 0; i < 30; i++) {
                const date = new Date(earliestDate);
                date.setDate(date.getDate() + i);
                itemAvailableDates.push(this.formatDateForBackend(date));
              }
            }
            break;
        }

        // For each item, add its available dates to the map
        // Only dates that are available for ALL items will remain
        if (itemAvailableDates.length > 0) {
          if (availableDatesMap.size === 0) {
            // First item - add all its dates
            itemAvailableDates.forEach((dateStr) => {
              const date = DateService.createDateFromApiFormat(dateStr);
              availableDatesMap.set(dateStr, date);
            });
          } else {
            // Subsequent items - only keep dates that exist in both
            const commonDates = new Map<string, Date>();
            itemAvailableDates.forEach((dateStr) => {
              if (availableDatesMap.has(dateStr)) {
                const date = DateService.createDateFromApiFormat(dateStr);
                commonDates.set(dateStr, date);
              }
            });
            availableDatesMap.clear();
            commonDates.forEach((date, dateStr) => {
              availableDatesMap.set(dateStr, date);
            });
          }
        }
      }

      // Convert to array and sort
      const availableDates = Array.from(availableDatesMap.values());
      return availableDates.sort((a, b) => a.getTime() - b.getTime());
    } catch (error) {
      console.error(
        "Error getting available dates for delivery method:",
        error
      );
      return [];
    }
  }
}
